<template>
  <v-container fluid class="reporte-page">

    <!-- ============================================================
         CABECERA / CONTROLES
    ============================================================= -->
    <v-card class="mb-5 control-card" outlined>
      <v-card-title class="d-flex align-center">
        <v-icon color="#C62828" class="mr-2">
          mdi-file-chart
        </v-icon>

        <span>Reporte de Localía por Proveedor</span>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-row align="center">

          <!-- SELECTOR PROVEEDOR -->
          <v-col cols="12" md="7">
            <v-autocomplete
              v-model="empresaSeleccionada"
              :items="empresas"
              :loading="cargandoEmpresas"
              item-text="text"
              item-value="value"
              label="Seleccione un proveedor"
              placeholder="Buscar por razón social"
              prepend-inner-icon="mdi-domain"
              outlined
              dense
              clearable
              hide-details
              @change="cambiarEmpresa"
            />
          </v-col>

          <!-- VISUALIZAR -->
          <v-col cols="12" md="5">
            <div class="d-flex flex-wrap">

              <v-btn
                color="primary"
                class="mr-2"
                :loading="cargandoReporte"
                :disabled="!empresaSeleccionada"
                @click="cargarReporte"
              >
                <v-icon left>
                  mdi-eye
                </v-icon>

                Visualizar
              </v-btn>

              <!-- PDF -->
              <v-btn
                color="#C62828"
                dark
                :disabled="!reporte"
                :loading="generandoPdf"
                @click="descargarPdf"
              >
                <v-icon left>
                  mdi-file-pdf-box
                </v-icon>

                Descargar PDF
              </v-btn>

            </div>
          </v-col>

        </v-row>
      </v-card-text>
    </v-card>


    <!-- ============================================================
         MENSAJE INICIAL
    ============================================================= -->
    <v-card
      v-if="!reporte && !cargandoReporte"
      outlined
      class="pa-10 text-center empty-card"
    >
      <v-icon
        size="70"
        color="grey lighten-1"
      >
        mdi-file-search-outline
      </v-icon>

      <div class="empty-title mt-4">
        Seleccione un proveedor
      </div>

      <div class="grey--text mt-2">
        Seleccione un proveedor y presione
        <strong>Visualizar</strong> para generar el informe.
      </div>
    </v-card>


    <!-- ============================================================
         LOADING
    ============================================================= -->
    <v-card
      v-if="cargandoReporte"
      outlined
      class="pa-10 text-center"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="55"
      />

      <div class="mt-4 grey--text">
        Generando informe...
      </div>
    </v-card>


    <!-- ============================================================
         REPORTE
    ============================================================= -->
    <div
      v-if="reporte && !cargandoReporte"
      class="preview-container"
    >

      <!-- Esta sección será capturada por html2canvas -->
      <div
        id="reporte-localia"
        ref="reporteLocalia"
        class="reporte-localia"
      >

        <!-- DECORACIÓN SUPERIOR -->
        <div class="decoracion-superior"></div>


        <!-- ========================================================
             TÍTULO
        ========================================================= -->
        <div class="titulo-reporte">
          <span class="numero-titulo">
            01.-
          </span>

          INFORME DETALLADO POR EMPRESA
        </div>


        <!-- ========================================================
             INFORMACIÓN SUPERIOR
        ========================================================= -->
        <div class="contenido-superior">

          <!-- ======================================================
               COLUMNA IZQUIERDA
          ======================================================= -->
          <div class="bloque-izquierdo">

            <!-- PROVEEDOR -->
            <div class="fila proveedor-fila">

              <div class="label label-oscuro">
                Proveedor
              </div>

              <div class="valor proveedor-nombre">
                {{ reporte.razonsocial || '-' }}
              </div>

            </div>


            <!-- ESPACIO -->
            <div class="separador-datos"></div>


            <!-- FECHA PRESENTACIÓN -->
            <div class="fila">

              <div class="label label-rojo">
                Fecha de Presentación
              </div>

              <div class="valor">
                {{ formatearFecha(reporte.fecha_presentacion) }}
              </div>

            </div>


            <!-- RESULTADO LOCALÍA -->
            <div class="fila">

              <div class="label label-rojo">
                Resultado Final de Localía
              </div>

              <div class="valor">
                {{ reporte.resultado_final_localia || '-' }}
              </div>

            </div>


            <!-- FECHA DIAGNÓSTICO -->
            <div class="fila">

              <div class="label label-rojo">
                Fecha de Diagnóstico
              </div>

              <div class="valor">
                {{ formatearFecha(reporte.fecha_diagnostico) }}
              </div>

            </div>


            <!-- FECHA INDUCCIÓN -->
            <div class="fila">

              <div class="label label-rojo">
                Fecha de Inducción al SGP
              </div>

              <div class="valor">
                {{ formatearFecha(reporte.fecha_induccion_sgp) }}
              </div>

            </div>


            <!-- FECHA CREDENCIALES -->
            <div class="fila">

              <div class="label label-rojo">
                Fecha de Envío de Credenciales
              </div>

              <div class="valor">
                {{ formatearFecha(reporte.fecha_envio_credenciales) }}
              </div>

            </div>

          </div>


          <!-- ======================================================
               COLUMNA DERECHA
          ======================================================= -->
          <div class="bloque-derecho">

            <!-- MONTO -->
            <div class="fila monto-fila">

              <div class="label label-rojo monto-label">
                Monto Total Contratado
              </div>

              <div class="valor monto-valor">
                {{ formatearMonto(reporte.monto_total_contratado) }}
              </div>

            </div>


            <!-- ESPACIO -->
            <div class="separador-datos"></div>


            <!-- PROCESOS -->
            <div class="procesos-wrapper">

              <div class="procesos-label">
                Actualmente, participando en
                procesos de postulación con
              </div>


              <div class="procesos-lista">

                <template
                  v-if="
                    reporte.procesos_postulacion &&
                    reporte.procesos_postulacion.length > 0
                  "
                >

                  <div
                    v-for="(proceso, index) in reporte.procesos_postulacion"
                    :key="
                      proceso.idpostulacion ||
                      proceso.idrequerimiento ||
                      index
                    "
                    class="proceso-fila"
                  >

                    <!-- CONTRATISTA -->
                    <div class="proceso-empresa">

                      {{
                        proceso.empresa_contratante ||
                        proceso.contratista ||
                        '-'
                      }}

                    </div>


                    <!-- SERVICIO -->
                    <div class="proceso-servicio">

                      {{
                        proceso.servicio ||
                        proceso.nombre_categoria ||
                        proceso.rubro ||
                        '-'
                      }}

                    </div>

                  </div>

                </template>


                <!-- SIN POSTULACIONES -->
                <div
                  v-else
                  class="sin-procesos"
                >
                  Actualmente no participa en procesos
                  de postulación.
                </div>

              </div>

            </div>

          </div>

        </div>


        <!-- ========================================================
             TABLA INFERIOR
             SOLO ESTRUCTURA POR EL MOMENTO
        ========================================================= -->
        <div class="tabla-wrapper">

          <table class="tabla-contratos">

            <thead>
              <tr>

                <th>
                  Empresa Contratante
                </th>

                <th>
                  Razón Social del Proveedor
                </th>

                <th class="col-monto">
                  Monto
                </th>

                <th>
                  Estado del Contrato
                </th>

                <th class="col-servicio">
                  Rubro-Servicio Específico Brindado
                </th>

                <th>
                  Fecha de Inicio del Contrato
                  <br>
                  Fecha de Primera Factura
                  <br>
                  (dd/mm/aaaa)
                </th>

              </tr>
            </thead>


            <!--
              IMPORTANTE:

              Por requerimiento funcional, esta tabla se mantiene
              únicamente como estructura visual.

              Todavía NO se carga información de contratos.
            -->
            <tbody>

              <tr
                v-for="contrato in reporte.contratos"
                :key="contrato.idpostulacion"
              >

                <td>
                  {{ contrato.empresa_contratante || '-' }}
                </td>

                <td>
                  {{ contrato.razon_social_proveedor || '-' }}
                </td>

                <td>
                  {{ formatearMonto(contrato.monto) }}
                </td>

                <td>
                  {{ contrato.estado_contrato || '-' }}
                </td>

                <td>
                  {{ contrato.servicio || '-' }}
                </td>

                <td>
                  {{
                    formatearFecha(
                      contrato.fecha_primera_factura ||
                      contrato.fecha_inicio_contrato
                    )
                  }}
                </td>

              </tr>


              <tr
                v-if="
                  !reporte.contratos ||
                  reporte.contratos.length === 0
                "
              >
                <td colspan="6">
                  No se encontraron contratos adjudicados.
                </td>
              </tr>

            </tbody>

          </table>

        </div>


        <!-- ========================================================
             PIE DEL REPORTE
        ========================================================= -->
        <div class="pie-reporte">

          <div class="numero-pagina">
            1
          </div>

          <div class="marca">
            Sistema de Gestión de Proveedores
          </div>

          <div class="numero-seccion">
            3
          </div>

        </div>

      </div>

    </div>


    <!-- ============================================================
         SNACKBAR
    ============================================================= -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="4000"
      top
      right
    >
      {{ snackbarTexto }}

      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Cerrar
        </v-btn>
      </template>

    </v-snackbar>

  </v-container>
</template>


<script>
import axios from 'axios'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

export default {

  name: 'ReporteLocalia',

  data () {
    return {

      // ============================================================
      // SESIÓN
      // ============================================================
      idusuario: null,
      rol: null,

      // ============================================================
      // EMPRESAS
      // ============================================================
      empresas: [],
      empresaSeleccionada: null,

      cargandoEmpresas: false,
      cargandoReporte: false,
      generandoPdf: false,

      // ============================================================
      // REPORTE
      // ============================================================
      reporte: null,

      // Número de filas que se mostrarán en la tabla inferior.
      // Por ahora solamente son estructura visual.
      filasTablaVacia: 6,

      // ============================================================
      // MENSAJES
      // ============================================================
      snackbar: false,
      snackbarTexto: '',
      snackbarColor: 'success'
    }
  },


  // ==============================================================
  // CREATED
  // ==============================================================
  created () {
    this.idusuario = localStorage.getItem('idusuario')
    this.rol = localStorage.getItem('rol')
  },


  // ==============================================================
  // MOUNTED
  // ==============================================================
  mounted () {
    this.selectEmpresas()
  },


  methods: {

    // ============================================================
    // LISTAR EMPRESAS
    // ============================================================
    selectEmpresas () {
      this.empresas = []
      this.cargandoEmpresas = true

      return axios
        .get('api/Empresas/SelectEmpresas')
        .then(res => {
          this.empresas = res.data.map(x => ({
            text: x.razonsocial,
            value: x.idempresa
          }))

          // En esta pantalla NO cargaría automáticamente
          // el primer proveedor.
          // Dejo que el usuario seleccione uno.
        })
        .catch(err => {
          console.error('Error cargando empresas:', err)

          this.mostrarMensaje(
            'No se pudo cargar la lista de proveedores.',
            'error'
          )
        })
        .finally(() => {
          this.cargandoEmpresas = false
        })
    },


    // ============================================================
    // LIMPIAR REPORTE
    // ============================================================
    limpiarReporte () {
      this.reporte = null
    },


    // ============================================================
    // CARGAR REPORTE
    // ============================================================
    cargarReporte () {
      if (!this.empresaSeleccionada) {
        this.mostrarMensaje(
          'Seleccione un proveedor.',
          'warning'
        )

        return
      }

      this.cargandoReporte = true
      this.reporte = null

      axios
        .get(
          `api/Empresas/ReporteLocalia/${this.empresaSeleccionada}`
        )
        .then(res => {
          const data = res.data || {}

          /*
           * Garantizamos que procesos_postulacion siempre
           * sea un array para evitar errores en el template.
           */
          data.procesos_postulacion =
            data.procesos_postulacion || []

          this.reporte = data
        })
        .catch(error => {
          console.error(
            'Error al cargar reporte de localía:',
            error
          )

          this.mostrarMensaje(
            'No se pudo obtener la información del proveedor.',
            'error'
          )
        })
        .finally(() => {
          this.cargandoReporte = false
        })
    },
    cargarReporteDefault () {
      if (!this.empresaSeleccionada) {
        this.mostrarMensaje(
          'Seleccione un proveedor.',
          'warning'
        )
        return
      }

      this.cargandoReporte = true
      this.reporte = null

      // Simular pequeña espera para probar también el loading
      setTimeout(() => {
        this.reporte = {
          idempresa: this.empresaSeleccionada,

          // Toma el nombre real seleccionado del autocomplete
          razonsocial: this.nombreEmpresaSeleccionada,

          fecha_presentacion: '2025-06-02',
          resultado_final_localia: 'Local',

          fecha_diagnostico: '2026-06-08',
          fecha_induccion_sgp: '2026-06-16',
          fecha_envio_credenciales: '2026-06-16',

          monto_total_contratado: 162640.38,

          cantidad_contratos: 6,
          cantidad_contratos_curso: 2,

          procesos_postulacion: [
            {
              idpostulacion: 1,
              idrequerimiento: 101,
              empresa_contratante:
                'CONSORCIO CONSTRUCTOR PERU',
              servicio:
                'Maquinaria Pesada / MONTACARGA 7 TN',
              estado: 'EN_POSTULACION'
            },
            {
              idpostulacion: 2,
              idrequerimiento: 102,
              empresa_contratante:
                'SAN MARTIN CONTRATISTAS GENERALES S.A.',
              servicio:
                'Transporte Liviano y de Personal / ALQUILER DE BUS',
              estado: 'EN_EVALUACIÓN'
            }
          ]
        }

        this.cargandoReporte = false
      }, 300)
    },


    // ============================================================
    // FORMATEAR FECHA
    // ============================================================
    formatearFecha (fecha) {
      if (!fecha) {
        return '-'
      }

      /*
       * Evitamos:
       *
       * new Date("2026-06-08")
       *
       * porque dependiendo de la zona horaria podría
       * mostrarse el día anterior.
       */

      const texto = fecha.toString().substring(0, 10)

      const partes = texto.split('-')

      if (partes.length !== 3) {
        return fecha
      }

      const anio = partes[0]
      const mes = partes[1]
      const dia = partes[2]

      return `${dia}/${mes}/${anio}`
    },


    // ============================================================
    // FORMATEAR MONTO
    // ============================================================
    formatearMonto (monto) {
      if (
        monto === null ||
        monto === undefined ||
        monto === ''
      ) {
        return '$0.00'
      }

      const numero = Number(monto)

      if (isNaN(numero)) {
        return '$0.00'
      }

      return new Intl.NumberFormat(
        'en-US',
        {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      ).format(numero)
    },


    // ============================================================
    // DESCARGAR PDF
    // ============================================================
    async descargarPdf () {
      if (!this.reporte) {
        return
      }

      const elemento = this.$refs.reporteLocalia

      if (!elemento) {
        this.mostrarMensaje(
          'No se encontró el contenido del reporte.',
          'error'
        )

        return
      }

      this.generandoPdf = true

      try {
        /*
         * Scale 2 mejora bastante la resolución del texto.
         *
         * Si el PDF pesa demasiado puedes bajarlo a 1.5.
         */
        const canvas = await html2canvas(
          elemento,
          {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            backgroundColor: '#FFFFFF',
            logging: false
          }
        )

        const imagen = canvas.toDataURL(
          'image/jpeg',
          0.95
        )

        // A4 horizontal
        const pdf = new jsPDF(
          'landscape',
          'mm',
          'a4'
        )

        const anchoPagina =
          pdf.internal.pageSize.getWidth()

        const altoPagina =
          pdf.internal.pageSize.getHeight()

        /*
         * Pequeño margen para evitar que el contenido
         * llegue exactamente al límite del PDF.
         */
        const margen = 5

        const anchoDisponible =
          anchoPagina - (margen * 2)

        const altoDisponible =
          altoPagina - (margen * 2)

        const proporcionCanvas =
          canvas.width / canvas.height

        const proporcionPagina =
          anchoDisponible / altoDisponible

        let anchoImagen
        let altoImagen

        /*
         * Mantener proporción para que el reporte
         * no se deforme.
         */
        if (proporcionCanvas > proporcionPagina) {
          anchoImagen = anchoDisponible
          altoImagen =
            anchoDisponible / proporcionCanvas
        } else {
          altoImagen = altoDisponible
          anchoImagen =
            altoDisponible * proporcionCanvas
        }

        // Centrar
        const posicionX =
          (anchoPagina - anchoImagen) / 2

        const posicionY =
          (altoPagina - altoImagen) / 2

        pdf.addImage(
          imagen,
          'JPEG',
          posicionX,
          posicionY,
          anchoImagen,
          altoImagen
        )

        const nombreEmpresa =
          this.limpiarNombreArchivo(
            this.reporte.razonsocial ||
            'Proveedor'
          )

        pdf.save(
          `Informe_Localia_${nombreEmpresa}.pdf`
        )

        this.mostrarMensaje(
          'Reporte generado correctamente.',
          'success'
        )
      } catch (error) {
        console.error(
          'Error generando PDF:',
          error
        )

        this.mostrarMensaje(
          'Ocurrió un error al generar el PDF.',
          'error'
        )
      } finally {
        this.generandoPdf = false
      }
    },


    // ============================================================
    // LIMPIAR NOMBRE PARA ARCHIVO
    // ============================================================
    limpiarNombreArchivo (nombre) {
      return nombre
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9_-]/g, '_')
        .replace(/_+/g, '_')
    },


    // ============================================================
    // MENSAJES
    // ============================================================
    mostrarMensaje (texto, color = 'success') {
      this.snackbarTexto = texto
      this.snackbarColor = color
      this.snackbar = true
    },
    cambiarEmpresa () {
      this.reporte = null
    },
  }
}
</script>


<style scoped>

/* ================================================================
   PÁGINA
================================================================ */

.reporte-page {
  background: #f5f6f8;
  min-height: 100vh;
}

.control-card {
  border-radius: 10px;
}

.empty-card {
  border-radius: 10px;
}

.empty-title {
  font-size: 20px;
  font-weight: 600;
  color: #424242;
}


/* ================================================================
   CONTENEDOR PREVIEW
================================================================ */

.preview-container {
  overflow-x: auto;
  padding: 20px 0 40px 0;
}


/*
 * Proporción aproximada 16:9 tipo PPT.
 *
 * Se utiliza ancho fijo para mantener la misma distribución
 * tanto en pantalla como al capturar con html2canvas.
 */
.reporte-localia {
  width: 1280px;
  min-height: 720px;

  margin: 0 auto;

  background: #ffffff;

  position: relative;

  padding:
    32px
    55px
    25px
    55px;

  box-sizing: border-box;

  font-family:
    Arial,
    Helvetica,
    sans-serif;

  color: #222;

  box-shadow:
    0 3px 18px
    rgba(0, 0, 0, 0.12);
}


/* ================================================================
   DECORACIÓN SUPERIOR
================================================================ */

.decoracion-superior {
  position: absolute;

  top: 0;
  right: 0;

  width: 420px;
  height: 95px;

  opacity: 0.13;

  background-image:
    radial-gradient(
      #9e9e9e 1.5px,
      transparent 1.5px
    );

  background-size: 10px 10px;
}


/* ================================================================
   TÍTULO
================================================================ */

.titulo-reporte {
  position: relative;

  color: #d22630;

  font-size: 31px;
  font-weight: 700;

  letter-spacing: 0.2px;

  margin-bottom: 65px;
}

.numero-titulo {
  font-weight: 400;
}


/* ================================================================
   INFORMACIÓN SUPERIOR
================================================================ */

.contenido-superior {
  display: flex;

  align-items: flex-start;

  gap: 95px;

  padding:
    0
    30px;

  margin-bottom: 20px;
}


/* ================================================================
   IZQUIERDA
================================================================ */

.bloque-izquierdo {
  width: 420px;
  flex-shrink: 0;
}


/* ================================================================
   DERECHA
================================================================ */

.bloque-derecho {
  width: 600px;
  flex-shrink: 0;
}


/* ================================================================
   FILAS
================================================================ */

.fila {
  display: flex;

  min-height: 29px;

  border:
    1px
    solid
    #8d8d8d;

  border-bottom: 0;
}

.fila:last-child {
  border-bottom:
    1px
    solid
    #8d8d8d;
}


.label {
  width: 200px;

  flex-shrink: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 5px 8px;

  text-align: center;

  font-size: 12px;
  font-weight: 600;

  box-sizing: border-box;
}

.valor {
  flex: 1;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 5px 8px;

  text-align: center;

  font-size: 12px;

  background: white;

  border-left:
    1px
    solid
    #8d8d8d;

  box-sizing: border-box;
}


/* ================================================================
   COLORES LABELS
================================================================ */

.label-rojo {
  background: #d22630;
  color: white;
}

.label-oscuro {
  background: #292929;
  color: white;
}


/* ================================================================
   PROVEEDOR
================================================================ */

.proveedor-fila {
  min-height: 25px;
}

.proveedor-nombre {
  font-weight: 700;
}


/* ================================================================
   SEPARACIÓN
================================================================ */

.separador-datos {
  height: 18px;
}


/* ================================================================
   MONTO
================================================================ */

.monto-fila {
  width: 450px;
  min-height: 25px;
}

.monto-label {
  width: 180px;
}

.monto-valor {
  font-weight: 700;
}


/* ================================================================
   PROCESOS
================================================================ */

.procesos-wrapper {
  display: flex;

  width: 100%;

  min-height: 135px;

  border:
    1px
    solid
    #8d8d8d;
}

.procesos-label {
  width: 180px;

  flex-shrink: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  background: #d22630;
  color: white;

  padding: 10px;

  text-align: center;

  font-size: 12px;
  line-height: 1.3;

  box-sizing: border-box;
}

.procesos-lista {
  flex: 1;

  background: white;
}

.proceso-fila {
  display: grid;

  grid-template-columns:
    1.4fr
    1fr;

  min-height: 45px;

  border-bottom:
    1px
    solid
    #8d8d8d;
}

.proceso-fila:last-child {
  border-bottom: 0;
}

.proceso-empresa,
.proceso-servicio {
  display: flex;

  align-items: center;
  justify-content: center;

  text-align: center;

  padding: 5px 8px;

  font-size: 11px;
  line-height: 1.25;

  box-sizing: border-box;
}

.proceso-empresa {
  font-weight: 600;

  border-right:
    1px
    solid
    #8d8d8d;
}

.sin-procesos {
  min-height: 133px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 15px;

  text-align: center;

  color: #757575;

  font-size: 12px;
}


/* ================================================================
   TABLA CONTRATOS
================================================================ */

.tabla-wrapper {
  padding:
    0
    30px;

  margin-top: 15px;
}

.tabla-contratos {
  width: 100%;

  border-collapse: collapse;

  table-layout: fixed;

  font-size: 11px;
}

.tabla-contratos th {
  background: #d22630;
  color: white;

  border:
    1px
    solid
    #d22630;

  padding: 8px 5px;

  text-align: center;

  font-weight: 600;

  line-height: 1.2;
}

.tabla-contratos td {
  height: 27px;

  padding: 4px;

  border:
    1px
    solid
    #777;

  text-align: center;

  background: white;
}

.tabla-contratos th:nth-child(1) {
  width: 18%;
}

.tabla-contratos th:nth-child(2) {
  width: 17%;
}

.tabla-contratos th:nth-child(3) {
  width: 10%;
}

.tabla-contratos th:nth-child(4) {
  width: 13%;
}

.tabla-contratos th:nth-child(5) {
  width: 25%;
}

.tabla-contratos th:nth-child(6) {
  width: 17%;
}


/* ================================================================
   PIE
================================================================ */

.pie-reporte {
  position: absolute;

  left: 65px;
  right: 65px;
  bottom: 18px;

  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  font-size: 11px;
  color: #777;
}

.marca {
  font-size: 15px;
  font-weight: 600;
  color: #555;
}

.numero-seccion {
  color: #d22630;
}


/* ================================================================
   RESPONSIVE
================================================================ */

@media (max-width: 1350px) {

  /*
   * No reducimos internamente el reporte porque alteraría
   * la composición que posteriormente captura html2canvas.
   *
   * El contenedor superior proporciona scroll horizontal.
   */

  .preview-container {
    overflow-x: auto;
  }
}


/* ================================================================
   IMPRESIÓN
================================================================ */

@media print {

  .control-card {
    display: none !important;
  }

  .reporte-page {
    background: white;
  }

  .preview-container {
    padding: 0;
    overflow: visible;
  }

  .reporte-localia {
    box-shadow: none;
    margin: 0;
  }
}

</style>